import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import CompanyChip from './asset-company/CompanyChip';
import './Assets.css';

/**
 * Diagnostics: asking a device's agent to run checks off the fixed menu, and
 * reading what came back.
 *
 * One component for three places, told apart by `path`:
 *
 *   /assets/:id/diagnostics    the device's page - every request, any ticket
 *   /tickets/:id/diagnostics   a ticket, staff or contractor - only the
 *                              requests made for that ticket
 *
 * The SERVER decides the menu: a register writer gets all of it, the
 * contractor on the ticket the reduced set, and anybody else nothing - in
 * which case there is no "Ask the device" button, and when nothing has been
 * asked either, the component draws nothing at all.
 *
 * Nothing here runs anything. It queues a request; the agent picks it up on
 * its next check-in, shows the person at the machine a notice naming who
 * asked, and sends back what it read. While a request is out, this polls.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const when = (t) => (t ? new Date(t).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '');

const STATE = {
  waiting: { word: 'Waiting for the device', tone: 'info' },
  running: { word: 'Running on the device', tone: 'info' },
  answered: { word: 'Answered', tone: 'success' },
  expired: { word: 'Not collected — the device never checked in', tone: 'warning' }
};

/** Why nothing can be asked, in words for whoever is looking. */
const whyNotWords = (why, admin) => ({
  disabled: admin
    ? 'Diagnostics are switched off. Turn on “Allow diagnostics on request” in Settings → Assets & devices → Device agent.'
    : 'IT has not switched diagnostics on yet.',
  contractor_not_allowed: 'The firm has not allowed contractors to ask for diagnostics. IT can still run them.',
  finished: admin ? 'This ticket is finished. Ask from the device’s own page instead.' : 'This ticket is finished.',
  no_agent: 'This device has no Jarvis agent installed, so there is nothing to ask.'
}[why] || null);

const ATTACHED_WORD = { monitor: 'Screen', dock: 'Dock', printer: 'Printer', storage: 'Drive', keyboard: 'Keyboard',
  mouse: 'Mouse', webcam: 'Webcam', audio: 'Audio', hub: 'Hub', other: 'Other' };

const RUN_STATUS = {
  timeout: 'did not finish within a minute',
  failed: 'could not run',
  unsupported: 'this agent cannot run this check'
};

export default function Diagnostics({ path, authHeaders = null, title = 'Diagnostics' }) {
  const [data, setData] = useState(null);
  const [menu, setMenu] = useState([]);
  const [asking, setAsking] = useState(false);
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  // Keyed on the header values, not the object: a page that builds its
  // headers afresh on every render would otherwise reload this forever.
  const authKey = authHeaders ? JSON.stringify(authHeaders) : '';
  const config = authKey ? { headers: JSON.parse(authKey) } : undefined;

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}${path}`, authKey ? { headers: JSON.parse(authKey) } : undefined);
      setData(r.data);
      if (Array.isArray(r.data.menu)) {
        setMenu(r.data.menu);
      } else if (r.data.may_ask) {
        const m = await axios.get(`${API_BASE}/diagnostics/menu`);
        setMenu(m.data.menu || []);
      } else {
        setMenu([]);
      }
    } catch {
      // A database without migration 046 answers 503: no diagnostics yet,
      // and nothing on screen about them.
      setData(null);
    }
  }, [path, authKey]);

  useEffect(() => { load(); }, [load]);

  const out = (data?.requests || []).some((r) => r.state === 'waiting' || r.state === 'running');
  useEffect(() => {
    if (!out) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [out, load]);

  if (!data) return null;
  const requests = data.requests || [];
  const blocked = !menu.length ? whyNotWords(data.why_not, data.admin) : null;
  if (!requests.length && !menu.length && !blocked) return null;

  const toggle = (r) => setChosen((c) => (c.includes(r) ? c.filter((x) => x !== r) : [...c, r]));

  const ask = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await axios.post(`${API_BASE}${path}`, { routines: chosen }, config);
      setNote(r.data.note || '');
      setAsking(false);
      setChosen([]);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not ask the device.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="as-panel as-diag" style={{ marginBottom: '1rem' }} data-testid="diagnostics">
      <div className="as-diag-head">
        <h2>{title}</h2>
        {menu.length > 0 && !asking && (
          <button type="button" className="btn btn-secondary" onClick={() => { setAsking(true); setNote(''); }}>
            Ask the device
          </button>
        )}
        {/* No agent: shown greyed out, so it is clear why it cannot be used. */}
        {!menu.length && data.why_not === 'no_agent' && (
          <button type="button" className="btn btn-secondary" disabled data-testid="diagnostics-ask-off"
            title="This device has no Jarvis agent, so there is nothing to ask.">
            Ask the device
          </button>
        )}
      </div>

      {asking && (
        <div className="as-diag-ask">
          <p className="as-quiet">
            Read-only checks. The person at the machine sees a notice naming you; nothing on it is changed.
          </p>
          <ul className="as-diag-menu">
            {menu.map((m) => (
              <li key={m.routine}>
                <label className="as-check">
                  <input type="checkbox" checked={chosen.includes(m.routine)} onChange={() => toggle(m.routine)} />
                  <span><strong>{m.label}</strong><span className="as-sub">{m.what}</span></span>
                </label>
              </li>
            ))}
          </ul>
          {error && <p className="as-error" role="alert">{error}</p>}
          <div className="as-actions">
            <button type="button" className="btn btn-primary" disabled={!chosen.length || busy} onClick={ask}>
              {busy ? 'Asking…' : `Run ${chosen.length || ''} check${chosen.length === 1 ? '' : 's'}`}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => { setAsking(false); setError(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {note && <p className="as-note" data-tone="info">{note}</p>}

      {blocked && <p className="as-note" data-tone="info" data-testid="diagnostics-why-not">{blocked}</p>}
      {!requests.length && !asking && !blocked && <p className="as-quiet">Nothing has been asked of this device yet.</p>}
      {requests.map((r) => <Request key={r.id} r={r} />)}
    </section>
  );
}

function Request({ r }) {
  const [open, setOpen] = useState(false);
  const s = STATE[r.state] || STATE.waiting;
  const words = r.runs?.length ? r.runs.map((x) => x.label).join(', ') : r.routines.join(', ');
  return (
    <div className="as-diag-request">
      <button type="button" className="as-diag-summary" onClick={() => setOpen(!open)} aria-expanded={open}
        disabled={!r.runs?.length}>
        <span className="as-chip" data-tone={s.tone}>{s.word}</span>
        <span>{words}</span>
        <CompanyChip name={r.company_name} color={r.company_color} code={r.company_code} short />
        <span className="as-sub">
          {`Asked by ${r.requested_by_name || 'someone'}${r.by_contractor ? ' (contractor)' : ''}, ${when(r.requested_at)}`}
          {r.ticket_number ? ` · ${r.ticket_number}` : ''}
          {r.answered_at ? ` · answered ${when(r.answered_at)}` : ''}
        </span>
      </button>
      {open && r.runs.map((x) => (
        <div key={x.routine} className="as-diag-run">
          <h3>{x.label}</h3>
          {x.status === 'ok'
            ? <Output routine={x.routine} o={x.output || {}} />
            : <p className="as-note" data-tone="warning">{`This check ${RUN_STATUS[x.status] || x.status}${x.error ? `: ${x.error}` : '.'}`}</p>}
        </div>
      ))}
    </div>
  );
}

const gb = (v) => (v === undefined || v === null ? '—' : `${v} GB`);
const yes = (v) => (v === true ? 'Yes' : v === false ? 'No' : 'Not known');

function Table({ head, rows, empty, limit = 50 }) {
  const [all, setAll] = useState(false);
  if (!rows.length) return <p className="as-quiet">{empty}</p>;
  const shown = all ? rows : rows.slice(0, limit);
  return (
    <>
      <div className="as-table-wrap">
        <table className="as-table as-static">
          <thead><tr>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {shown.map((row, i) => (
              <tr key={i}>{row.map((c, j) => <td key={head[j]} data-label={head[j]}>{c}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > shown.length && (
        <button type="button" className="btn btn-secondary as-diag-more" onClick={() => setAll(true)}>
          {`Show all ${rows.length}`}
        </button>
      )}
    </>
  );
}

function Output({ routine, o }) {
  const [filter, setFilter] = useState('');
  switch (routine) {
    case 'uptime':
      return (
        <>
          <p>
            {o.uptime_hours !== undefined ? `Up ${o.uptime_hours} hours` : 'Uptime not known'}
            {o.last_boot ? `, since ${when(o.last_boot)}` : ''}.
            {o.pending_reboot ? ' A restart is waiting.' : ''}
          </p>
          <Table head={['Shut down', 'Cleanly']} empty="No shutdowns on record."
            rows={(o.shutdowns || []).map((s) => [s.at && !Number.isNaN(Date.parse(s.at)) ? when(s.at) : s.at, yes(s.clean)])} />
        </>
      );
    case 'updates':
      return (
        <>
          <p>
            {`${(o.pending || []).length} update${(o.pending || []).length === 1 ? '' : 's'} waiting`}
            {o.restart_needed ? '; a restart is needed' : ''}
            {o.last_success ? `. Last installed ${when(o.last_success)}` : ''}.
            {o.note ? ` ${o.note}` : ''}
          </p>
          <Table head={['Update', 'Size', 'Restart']} empty="Nothing waiting."
            rows={(o.pending || []).map((u) => [u.title, u.size_mb !== undefined ? `${u.size_mb} MB` : '—', yes(u.restart)])} />
        </>
      );
    case 'device_problems':
      return (
        <>
          {o.note && <p className="as-quiet">{o.note}</p>}
          <Table head={['Device', 'Error code', 'Driver']} empty="No hardware faults reported."
            rows={(o.devices || []).map((d) => [d.name, d.code, [d.driver_version, d.driver_date].filter(Boolean).join(', ') || '—'])} />
        </>
      );
    case 'processes': {
      const row = (p) => [p.name, p.publisher || '—', `${p.cpu_pct ?? 0}%`, `${p.memory_mb ?? 0} MB`];
      const head = ['Program', 'Publisher', 'Processor', 'Memory'];
      return (
        <>
          <p className="as-quiet">{`Measured over ${o.sample_seconds || 10} seconds.`}</p>
          <h4>Most processor</h4>
          <Table head={head} rows={(o.by_cpu || []).map(row)} empty="Nothing reported." />
          <h4>Most memory</h4>
          <Table head={head} rows={(o.by_memory || []).map(row)} empty="Nothing reported." />
        </>
      );
    }
    case 'memory_disk':
      return (
        <>
          <p>
            {`Memory: ${gb(o.ram_available_gb)} free of ${gb(o.ram_total_gb)}`}
            {o.commit_pct !== undefined ? `; ${o.commit_pct}% committed` : ''}.
          </p>
          <Table head={['Disk', 'Free', 'Size', 'Health']} empty="No disks reported."
            rows={(o.volumes || []).map((v) => [v.name, gb(v.free_gb), gb(v.size_gb), v.health || '—'])} />
        </>
      );
    case 'battery':
      if (!o.present) return <p>No battery.</p>;
      return (
        <p>
          {o.health_pct !== undefined ? `Holds ${o.health_pct}% of its design charge` : 'Health not reported'}
          {o.full_wh !== undefined ? ` (${o.full_wh} Wh${o.design_wh !== undefined ? ` of ${o.design_wh} Wh` : ''})` : ''}
          {o.cycles ? `, ${o.cycles} charge cycles` : ''}.
        </p>
      );
    case 'attached_devices':
      return (
        <Table head={['Kind', 'Device', 'Serial', 'Connection']} empty="Nothing plugged in."
          rows={(o.attached || []).map((a) => [ATTACHED_WORD[a.kind] || a.kind,
            [a.make, a.model || a.name].filter(Boolean).join(' ') || 'Unnamed',
            a.serial || '—', [a.bus, a.address, a.size_gb != null && `${a.size_gb} GB`].filter(Boolean).join(' · ')])} />
      );
    case 'network_scan':
      return (
        <>
          <p>{`${o.answered ?? '?'} of ${o.scanned ?? '?'} addresses answered${o.subnet ? ` on ${o.subnet}` : ''}. What they are is in the Discovery inbox.`}</p>
          <Table head={['Address', 'Name', 'MAC', 'Ports']} empty="Nothing answered."
            rows={(o.hosts || []).filter((h) => !h.self).map((h) => [h.ip, h.hostname || h.snmp?.name || '—', h.mac || '—', (h.ports || []).join(', ')])} />
        </>
      );
    case 'network':
      return (
        <>
          <dl className="as-facts">
            <dt>Gateway</dt><dd>{o.gateway ? `${o.gateway} — ${o.gateway_reachable ? 'answers' : 'does not answer'}` : 'None'}</dd>
            <dt>Wi-Fi signal</dt><dd>{o.wifi_signal_pct !== undefined ? `${o.wifi_signal_pct}%` : 'Not on Wi-Fi'}</dd>
            <dt>DNS</dt><dd>{(o.dns_servers || []).join(', ') || '—'}{o.dns_lookup_ms !== undefined ? ` · looked Jarvis up in ${o.dns_lookup_ms} ms` : ''}</dd>
            <dt>Jarvis</dt><dd>{o.jarvis_reachable ? `Reached in ${o.jarvis_ms} ms` : o.jarvis_reachable === false ? 'Could not be reached' : '—'}</dd>
          </dl>
          <Table head={['Connection', 'Up', 'Addresses', 'MAC']} empty="No connections reported."
            rows={(o.interfaces || []).filter((i) => i.up).map((i) => [i.name, 'Yes', (i.ips || []).join(', '), i.mac || '—'])} />
        </>
      );
    case 'recent_errors':
      return (
        <Table head={['Source', 'Count', 'Last', 'Message']} empty="No errors in the last 24 hours."
          rows={(o.sources || []).map((s) => [s.source, s.count, when(s.last?.at), s.last?.message || ''])} limit={20} />
      );
    case 'startup':
      return (
        <Table head={['Name', 'Kind', 'Publisher', 'Command']} empty="Nothing starts with the machine."
          rows={(o.items || []).map((i) => [i.name, i.kind || '—', i.publisher || '—', i.command || ''])} limit={30} />
      );
    case 'software': {
      const f = filter.trim().toLowerCase();
      const items = (o.items || []).filter((i) => !f || `${i.name} ${i.publisher}`.toLowerCase().includes(f));
      return (
        <>
          <input type="search" className="as-diag-filter" placeholder={`Filter ${(o.items || []).length} programs`}
            value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter installed programs" />
          <Table head={['Program', 'Version', 'Publisher', 'Installed']} empty="No programs match."
            rows={items.map((i) => [i.name, i.version || '—', i.publisher || '—', i.installed || '—'])} />
        </>
      );
    }
    case 'thermal':
      if (!o.available) return <p className="as-quiet">{o.note || 'Not available on this machine.'}</p>;
      return (
        <Table head={['Sensor', 'Reading']} empty="Nothing reported."
          rows={[...(o.sensors || []).map((s) => [s.name, `${s.celsius} °C`]), ...(o.fans || []).map((x) => [x.name, `${x.rpm} rpm`])]} />
      );
    default:
      return <pre className="as-diag-raw">{JSON.stringify(o, null, 2)}</pre>;
  }
}

/**
 * For the person a device belongs to: every request ever made against their
 * machines - who asked, what, when. Not the results; those stay with IT and
 * the ticket. Draws nothing until something has been asked.
 */
export function DiagnosticsOnMyDevices() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE}/assets/mine/diagnostics`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  if (!rows.length) return null;
  return (
    <section className="as-panel" style={{ marginTop: '1rem' }} data-testid="my-diagnostics">
      <h2>Checks run on your devices</h2>
      <p className="as-quiet">
        Every time IT or a contractor asked one of your devices for a diagnostic. They only read; nothing was changed.
      </p>
      <Table head={['When', 'Asked by', 'Checks', 'Device', 'For']} empty=""
        rows={rows.map((r) => [when(r.requested_at), `${r.requested_by_name || 'Someone'}${r.by_contractor ? ' (contractor)' : ''}`,
          r.routines.join(', '),
          <>{r.asset_tag || '—'}{r.company_name ? <> <CompanyChip name={r.company_name} color={r.company_color} code={r.company_code} short /></> : null}</>,
          r.ticket_number || '—'])} />
    </section>
  );
}
